// Admin Bulk Product CSV Upload — drag/drop, dry-run preview, commit.
import React, { useState } from "react";
import { Upload, Download, FileText, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const SAMPLE_HEADER = "name, category_slug, brand, generic_name, mrp, selling_price, pack_size, prescription_required, stock_qty, conditions (semicolons), tags (semicolons), short_description, image_urls (semicolons)";

const Content = () => {
    const [file, setFile] = useState(null);
    const [dryRun, setDryRun] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get("/admin/products/bulk-upload/template", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "pills-products-template.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download template");
        }
    };

    const analyze = async (selectedFile) => {
        if (!selectedFile) return;
        if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
            toast.error("Please select a .csv file");
            return;
        }
        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error("Max CSV size is 5MB");
            return;
        }
        setAnalyzing(true);
        setDryRun(null);
        const form = new FormData();
        form.append("file", selectedFile);
        try {
            const { data } = await api.post("/admin/products/bulk-upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDryRun(data);
            setFile(selectedFile);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not parse CSV");
        } finally { setAnalyzing(false); }
    };

    const commit = async () => {
        if (!file) return;
        setCommitting(true);
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post("/admin/products/bulk-upload?commit=true", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Inserted ${data.inserted} products`);
            setDryRun({ ...dryRun, committed: true, inserted: data.inserted });
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Commit failed");
        } finally { setCommitting(false); }
    };

    return (
        <div className="space-y-5" data-testid="bulk-upload-page">
            <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="label-uppercase">Bulk upload</p>
                        <h2 className="text-base font-semibold text-zinc-900">CSV import</h2>
                        <p className="mt-1 text-xs text-zinc-500">
                            Required columns: <code className="rounded bg-zinc-100 px-1 text-[10px]">name, category_slug, mrp, selling_price</code> · max 5 MB
                        </p>
                    </div>
                    <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50" data-testid="bulk-download-template">
                        <Download className="h-3 w-3" /> Download template
                    </button>
                </div>

                <label className="mt-5 flex w-full cursor-pointer flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50/50 px-6 py-10 transition-colors hover:border-brand-300 hover:bg-brand-50/40" data-testid="bulk-dropzone">
                    <Upload className="h-8 w-8 text-brand-700" />
                    <span className="text-sm font-medium text-zinc-700">{file ? file.name : "Drop your CSV or click to browse"}</span>
                    <span className="text-[10px] text-zinc-500">{SAMPLE_HEADER}</span>
                    <input type="file" accept=".csv" className="hidden" onChange={(e) => analyze(e.target.files?.[0])} data-testid="bulk-file-input" />
                </label>

                {analyzing && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-zinc-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Analyzing CSV…
                    </div>
                )}
            </div>

            {dryRun && (
                <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft" data-testid="bulk-dry-run-result">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 rounded-full bg-zinc-100 px-3 py-1.5 text-xs">
                            <FileText className="h-3 w-3 text-zinc-400" /> {dryRun.total_rows} row(s)
                        </div>
                        <div className="flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-medium text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" /> {dryRun.valid} valid
                        </div>
                        {dryRun.errors?.length > 0 && (
                            <div className="flex items-center gap-2 rounded-full bg-rose-100 px-3 py-1.5 text-xs font-medium text-rose-700">
                                <AlertCircle className="h-3 w-3" /> {dryRun.errors.length} errors
                            </div>
                        )}
                        <div className="ml-auto">
                            {dryRun.committed ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid="bulk-committed-pill">
                                    <CheckCircle2 className="h-3 w-3" /> Inserted {dryRun.inserted}
                                </span>
                            ) : (
                                <button
                                    disabled={committing || (dryRun.errors?.length || 0) > 0 || dryRun.valid === 0}
                                    onClick={commit}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-4 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                                    data-testid="bulk-commit-btn"
                                >
                                    {committing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                    Commit upload ({dryRun.valid})
                                </button>
                            )}
                        </div>
                    </div>

                    {dryRun.preview?.length > 0 && (
                        <div className="mt-4">
                            <p className="label-uppercase mb-2">Preview (first 5)</p>
                            <div className="overflow-x-auto rounded-xl border border-zinc-100">
                                <table className="w-full text-xs">
                                    <thead className="bg-zinc-50 text-left text-zinc-500">
                                        <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Brand</th><th className="px-3 py-2 text-right">MRP</th><th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-right">Stock</th><th className="px-3 py-2">Rx</th></tr>
                                    </thead>
                                    <tbody>
                                        {dryRun.preview.map((r, i) => (
                                            <tr key={i} className="border-t border-zinc-50">
                                                <td className="px-3 py-2 font-medium">{r.name}</td>
                                                <td className="px-3 py-2 text-zinc-500">{r.category_slug}</td>
                                                <td className="px-3 py-2 text-zinc-500">{r.brand}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">₹{r.mrp}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">₹{r.selling_price}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.stock_qty}</td>
                                                <td className="px-3 py-2">{r.prescription_required ? "Yes" : "No"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {dryRun.errors?.length > 0 && (
                        <div className="mt-5">
                            <p className="label-uppercase mb-2 text-rose-600">Errors</p>
                            <ul className="max-h-72 space-y-1 overflow-y-auto rounded-xl border border-rose-100 bg-rose-50/40 p-3 text-xs" data-testid="bulk-errors-list">
                                {dryRun.errors.slice(0, 50).map((e, i) => (
                                    <li key={i} className="flex items-start gap-1.5 text-rose-700">
                                        <span className="font-mono font-semibold">row {e.row_index}:</span>
                                        <span>{e.errors.join("; ")}</span>
                                    </li>
                                ))}
                                {dryRun.errors.length > 50 && <li className="text-zinc-500">… and {dryRun.errors.length - 50} more</li>}
                            </ul>
                        </div>
                    )}

                    {dryRun.total_rows > 500 && !dryRun.committed && (
                        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            ⚠️ This CSV has more than 500 rows; commit may take a few seconds.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

const AdminBulkUpload = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="Bulk product upload" subtitle="Import many products from CSV · auto-validates · per-row error reporting">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminBulkUpload;
